import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../landing.css';
import '../core-pages.css';
import '../themes.css';
// eslint-disable-next-line no-unused-vars
import { useSpring as useReactSpring, useInView, animated, config } from '@react-spring/web';
import { motion as Motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import {
  Zap,
  Sparkles,
  BarChart2,
  Layout,
  ArrowRight,
  ShieldCheck,
  Smartphone,
  MousePointer2,
  Lock,
  ArrowUpRight,
  Mail,
  MessageSquare,
  ShoppingBag,
  Globe,
  Share2,
  Coffee,
  ChevronDown
} from 'lucide-react';
import polyConfig from '../../poly.json';
import { cn } from '../lib/utils';
import { BloomFilter } from '../lib/bloomFilter';
import { toast } from '../lib/feedbackToast';
import { SEO_PAGES } from '../lib/seoPages';
import { fetchPublicProfile } from '../lib/publicProfileApi';
import PremiumProfileReveal from '../components/PremiumProfileReveal';
import CursorGlow from '../components/CursorGlow';
import LivingGlassNav from '../components/LivingGlassNav';
import { MOTION_TOKENS, PREMIUM_EASE } from '../lib/motionTokens';

let supabaseModuleLoader;
const loadSupabaseModule = () => {
  if (!supabaseModuleLoader) {
    supabaseModuleLoader = import('../lib/supabase');
  }
  return supabaseModuleLoader;
};
const SUPPORT_EMAIL = 'support.linkit@gmail.com';
const SUPPORT_COMPOSE_URL = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(SUPPORT_EMAIL)}&su=${encodeURIComponent('Linkit support')}`;
const FOOTER_GROWTH_LINKS = SEO_PAGES.filter((page) => page.group !== 'Template').slice(0, 5);
const FOOTER_TEMPLATE_LINKS = SEO_PAGES.filter((page) => page.group === 'Template');
const FOOTER_LEGAL_LINKS = [
  { to: '/privacy', label: 'Privacy' },
  { to: '/terms', label: 'Terms' },
  { to: '/copyright', label: 'Copyright' },
  { to: '/cookies', label: 'Cookies' },
  { to: '/guidelines', label: 'Rules' },
  { to: '/trademarks', label: 'Trademarks' },
];
const withoutFullStops = (value) => String(value || '').replaceAll('.', '');

const FeatureShowcase = ({ type, active }) => {
  if (type === 'design') {
    return (
      <div className="landing-feature-demo landing-feature-design" aria-hidden="true">
        <div className="landing-feature-browser">
          <div className="landing-feature-browser-bar"><span /><span /><span /></div>
          <div className="landing-feature-profile">
            <Motion.div
              className="landing-feature-avatar"
              animate={active ? { scale: [0.92, 1], opacity: [0.65, 1] } : {}}
              transition={MOTION_TOKENS.showcase}
            />
            <div className="landing-feature-copy-lines"><span /><span /></div>
            <div className="landing-feature-link-preview landing-feature-link-preview-primary" />
            <div className="landing-feature-link-preview" />
          </div>
        </div>
        <div className="landing-feature-palette">
          {['#6F2438', '#C69637', '#0E5E56'].map((color, index) => (
            <Motion.span
              key={color}
              style={{ backgroundColor: color }}
              animate={active ? { y: [10, 0], opacity: [0, 1] } : {}}
              transition={{ ...MOTION_TOKENS.component, delay: index * 0.08 }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (type === 'direction') {
    return (
      <div className="landing-feature-demo landing-feature-direction" aria-hidden="true">
        <div className="landing-routing-label">Visitor path</div>
        <div className="landing-routing-stack">
          {[
            ['Featured launch', '01'],
            ['Book a session', '02'],
            ['Latest work', '03'],
          ].map(([label, order], index) => (
            <Motion.div
              key={label}
              className={cn('landing-routing-row', index === 0 && 'landing-routing-row-active')}
              initial={false}
              animate={active ? { x: 0, opacity: 1 } : { x: 14, opacity: 0.55 }}
              transition={{ ...MOTION_TOKENS.component, delay: index * 0.07 }}
            >
              <span>{order}</span>
              <strong>{label}</strong>
              <ArrowUpRight size={15} />
            </Motion.div>
          ))}
        </div>
        <Motion.div
          className="landing-routing-cursor"
          animate={active ? { x: [0, 45, 45], y: [0, 25, 25], opacity: [0, 1, 0] } : {}}
          transition={{ duration: 1.4, delay: 0.35, ease: PREMIUM_EASE }}
        >
          <MousePointer2 size={22} fill="currentColor" />
        </Motion.div>
      </div>
    );
  }

  return (
    <div className="landing-feature-demo landing-feature-analytics" aria-hidden="true">
      <div className="landing-analytics-head">
        <div><span>Profile activity</span><strong>2,418</strong></div>
        <span className="landing-analytics-change">+28%</span>
      </div>
      <svg viewBox="0 0 320 116" role="presentation">
        <defs>
          <linearGradient id="landing-chart-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.26" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>
        <Motion.path
          d="M4 102 C42 96 48 72 80 78 C116 85 121 42 156 53 C189 64 202 22 238 32 C274 42 284 12 316 8 L316 116 L4 116 Z"
          fill="url(#landing-chart-fill)"
          initial={false}
          animate={active ? { opacity: 1 } : { opacity: 0.25 }}
          transition={MOTION_TOKENS.reveal}
        />
        <Motion.path
          d="M4 102 C42 96 48 72 80 78 C116 85 121 42 156 53 C189 64 202 22 238 32 C274 42 284 12 316 8"
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
          initial={false}
          animate={active ? { pathLength: 1, opacity: 1 } : { pathLength: 0.15, opacity: 0.4 }}
          transition={MOTION_TOKENS.showcase}
        />
      </svg>
      <div className="landing-analytics-metrics">
        <span><i />Profile views</span>
        <span><i />Link clicks</span>
      </div>
    </div>
  );
};

const MOTION = {
  entrance: { tension: 170, friction: 25, mass: 1 },
  settle: { tension: 150, friction: 27, mass: 1 },
  magnetic: { tension: 185, friction: 23, mass: 0.9 },
};

const DraggableMarqueeTicker = () => {
  const tickerItems = [
    'LINK PAGE',
    '\u2014',
    'SHOP',
    '\u2014',
    'TIPS',
    '\u2014',
    'PRIVATE CONTENT',
    '\u2014',
    'ANALYTICS',
    '\u2014',
    'SUBSCRIBERS',
    '\u2014',
    'DESIGN CONTROL',
    '\u2014',
    '0% PLATFORM COMMISSION',
    '\u2014',
  ];

  return (
    <div className="overflow-hidden py-5 relative z-30">
      <div className="landing-marquee-track flex w-max whitespace-nowrap select-none will-change-transform" aria-hidden="true">
        {[0, 1].map((group) => (
          <div key={group} className="flex shrink-0 items-center gap-8 pr-8">
            {tickerItems.map((text, index) => (
              <span
                key={`${group}-${index}`}
                className={`text-sm md:text-lg font-black uppercase tracking-[0.2em] shrink-0 ${
                  text === '\u2014' ? 'text-[#3F6F65] text-xl opacity-60' : 'text-[#0F4A45]'
                }`}
              >
                {text}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const Landing = () => {
  const [claimName, setClaimName] = useState('');
  const [bloom, setBloom] = useState(null);
  const [availability, setAvailability] = useState('idle');
  const [previewProfile, setPreviewProfile] = useState(null);
  const [previewLinks, setPreviewLinks] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [claimPulse, setClaimPulse] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [activeWorkflowStep, setActiveWorkflowStep] = useState(0);
  const [activeCtaCapability, setActiveCtaCapability] = useState(0);
  const [activeSupportRoute, setActiveSupportRoute] = useState(0);
  const [activeVaultCapability, setActiveVaultCapability] = useState(0);
  const [commerceSequenceStep, setCommerceSequenceStep] = useState(0);
  const [activeShareRoute, setActiveShareRoute] = useState(0);
  const [activeFeatureShowcase, setActiveFeatureShowcase] = useState(0);
  const [visibleShowcase, setVisibleShowcase] = useState(null);
  const [activeSection, setActiveSection] = useState('hero');
  
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const autoPauseUntil = useRef({});
  const reduceMotion = useReducedMotion();



  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistSent, setWaitlistSent] = useState(false);
  const [isAlreadyJoined, setIsAlreadyJoined] = useState(false);
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const activeCommerceMode = Math.floor(commerceSequenceStep / 3);
  const commerceStage = commerceSequenceStep % 3;

  // Active section tracking for navbar
  useEffect(() => {
    const sections = ['hero', 'features', 'commerce', 'workflow', 'vault', 'support'];
    const observers = [];
    sections.forEach((id) => {
      const el = id === 'hero' ? containerRef.current?.querySelector('.landing-hero') : document.getElementById(id);
      if (!el) return;
      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.15) setActiveSection(id);
      }, { threshold: [0.15], rootMargin: '-10% 0px -30% 0px' });
      observer.observe(el);
      observers.push(observer);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  const scrollToSection = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileMenuOpen(false);
  }, []);

  const pauseShowcase = (name, duration = 12000) => {
    autoPauseUntil.current[name] = Date.now() + duration;
  };

  const showcaseCanAdvance = useCallback((name) => Date.now() >= (autoPauseUntil.current[name] || 0), []);

  useEffect(() => {
    if (reduceMotion || typeof IntersectionObserver === 'undefined') return undefined;

    const sections = Array.from(document.querySelectorAll('[data-auto-showcase]'));
    const ratios = new Map();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        ratios.set(entry.target.dataset.autoShowcase, entry.isIntersecting ? entry.intersectionRatio : 0);
      });
      const next = [...ratios.entries()].sort((a, b) => b[1] - a[1])[0];
      setVisibleShowcase(next && next[1] >= 0.28 ? next[0] : null);
    }, {
      threshold: [0, 0.28, 0.42, 0.62],
      rootMargin: '-8% 0px -18% 0px',
    });

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [reduceMotion]);

  useEffect(() => {
    if (reduceMotion || visibleShowcase !== 'features') return undefined;
    const timer = window.setInterval(() => {
      if (showcaseCanAdvance('features')) setActiveFeatureShowcase((current) => (current + 1) % 3);
    }, 4400);
    return () => window.clearInterval(timer);
  }, [reduceMotion, showcaseCanAdvance, visibleShowcase]);

  useEffect(() => {
    if (reduceMotion || visibleShowcase !== 'vault') return undefined;
    const timer = window.setInterval(() => {
      if (showcaseCanAdvance('vault')) setActiveVaultCapability((current) => (current + 1) % 4);
    }, 4600);
    return () => window.clearInterval(timer);
  }, [reduceMotion, showcaseCanAdvance, visibleShowcase]);

  useEffect(() => {
    if (reduceMotion || visibleShowcase !== 'commerce') return undefined;
    const timer = window.setInterval(() => {
      if (!showcaseCanAdvance('commerce')) return;
      setCommerceSequenceStep((current) => (current + 1) % 9);
    }, 1800);
    return () => window.clearInterval(timer);
  }, [reduceMotion, showcaseCanAdvance, visibleShowcase]);

  useEffect(() => {
    if (reduceMotion || visibleShowcase !== 'share') return undefined;
    const timer = window.setInterval(() => {
      if (showcaseCanAdvance('share')) setActiveShareRoute((current) => (current + 1) % 4);
    }, 3800);
    return () => window.clearInterval(timer);
  }, [reduceMotion, showcaseCanAdvance, visibleShowcase]);

  useEffect(() => {
    if (reduceMotion || visibleShowcase !== 'workflow') return undefined;
    const timer = window.setInterval(() => {
      if (showcaseCanAdvance('workflow')) setActiveWorkflowStep((current) => (current + 1) % 3);
    }, 4400);
    return () => window.clearInterval(timer);
  }, [reduceMotion, showcaseCanAdvance, visibleShowcase]);

  useEffect(() => {
    if (reduceMotion || visibleShowcase !== 'cta') return undefined;
    const timer = window.setInterval(() => {
      if (showcaseCanAdvance('cta')) setActiveCtaCapability((current) => (current + 1) % 4);
    }, 4800);
    return () => window.clearInterval(timer);
  }, [reduceMotion, showcaseCanAdvance, visibleShowcase]);

  useEffect(() => {
    if (reduceMotion || visibleShowcase !== 'support') return undefined;
    const timer = window.setInterval(() => {
      if (showcaseCanAdvance('support')) setActiveSupportRoute((current) => (current + 1) % 4);
    }, 4600);
    return () => window.clearInterval(timer);
  }, [reduceMotion, showcaseCanAdvance, visibleShowcase]);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!waitlistEmail.trim() || !waitlistEmail.includes('@')) return;
    setWaitlistSubmitting(true);
    try {
      const res = await fetch('/api/mail?action=waitlist', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          [polyConfig.HEADER_NAME]: polyConfig.HEADER_VALUE
        },
        body: JSON.stringify({ email: waitlistEmail.trim(), source: 'landing' }),
      });
      const data = await res.json();
      if (res.ok) {
        setWaitlistSent(true);
        if (data.alreadyJoined) {
          setIsAlreadyJoined(true);
          toast.info('Already Joined', { description: 'This email is already on the roadmap list' });
        } else {
          toast.success('You Are On The List', { description: 'We will email you when new Linkit features roll out' });
        }
      } else {
        toast.error('Request Failed', { description: 'Could not join the roadmap list right now' });
      }
    } catch {
      toast.error('Network Error', { description: 'Could not join the roadmap list right now' });
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  // Auth redirect
  React.useEffect(() => {
    let cancelled = false;
    let subscription;
    let idleHandle;
    let timeoutHandle;

    const checkAuth = async () => {
      const { supabase } = await loadSupabaseModule();
      if (cancelled) return;
      const { data: { session } } = await supabase.auth.getSession();
      if (!cancelled && session) navigate('/dashboard');

      const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
        if (!cancelled && event === 'SIGNED_IN' && session) navigate('/dashboard');
      });
      subscription = authListener.subscription;
    };

    if (window.requestIdleCallback) {
      idleHandle = window.requestIdleCallback(checkAuth, { timeout: 1200 });
    } else {
      timeoutHandle = window.setTimeout(checkAuth, 250);
    }

    return () => {
      cancelled = true;
      if (idleHandle) window.cancelIdleCallback?.(idleHandle);
      if (timeoutHandle) window.clearTimeout(timeoutHandle);
      subscription?.unsubscribe();
    };
  }, [navigate]);

  // Username availability
  React.useEffect(() => {
    const shouldLoadBloom = !bloom && claimName.trim().length >= 3;
    if (!shouldLoadBloom) return undefined;

    let cancelled = false;
    const initBloom = async () => {
      const { fetchAllUsernames } = await loadSupabaseModule();
      if (cancelled) return;
      const data = await fetchAllUsernames();
      if (cancelled) return;
      const bf = new BloomFilter();
      bf.addAll(data || []);
      setBloom(bf);
    };
    initBloom();

    return () => {
      cancelled = true;
    };
  }, [bloom, claimName]);

  const checkUsername = React.useCallback(async (val) => {
    if (!val || val.length < 3) { setAvailability('idle'); return; }
    if (!bloom) return;
    const maybeTaken = bloom.check(val);
    if (!maybeTaken) { setAvailability('available'); return; }
    setAvailability('verifying');
    const { supabase } = await loadSupabaseModule();
    const { data } = await supabase.from('profiles').select('username').eq('username', val.toLowerCase()).single();
    setAvailability(data ? 'taken' : 'available');
  }, [bloom]);

  React.useEffect(() => {
    const timer = setTimeout(() => { checkUsername(claimName); }, 300);
    return () => clearTimeout(timer);
  }, [claimName, checkUsername]);

  React.useEffect(() => {
    const username = claimName.trim().toLowerCase();
    let cancelled = false;

    setPreviewProfile(null);
    setPreviewLinks([]);

    if (availability !== 'taken' || username.length < 3) {
      setPreviewLoading(false);
      return undefined;
    }

    setPreviewLoading(true);
    fetchPublicProfile(username)
      .then((payload) => {
        if (cancelled) return;
        setPreviewProfile(payload?.profile || null);
        setPreviewLinks(Array.isArray(payload?.links) ? payload.links : []);
      })
      .catch(() => {
        if (cancelled) return;
        setPreviewProfile(null);
        setPreviewLinks([]);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [availability, claimName]);

  React.useEffect(() => {
    if (!claimName || availability === 'idle') return undefined;
    setClaimPulse(true);
    const timer = setTimeout(() => setClaimPulse(false), 460);
    return () => clearTimeout(timer);
  }, [availability, claimName]);

  const handleClaim = () => {
    if (!claimName?.trim()) { toast.error('Identity required'); return; }
    if (claimName.length < 3) { toast.error('Too short'); return; }
    if (availability === 'taken') { toast.error('Already taken'); return; }
    navigate(`/login?intent=${claimName}`);
  };

  // Animations
  const titleAnim = useReactSpring({ from: { opacity: 0, y: 34 }, to: { opacity: 1, y: 0 }, delay: 280, config: MOTION_TOKENS.entrance });
  const line2Anim = useReactSpring({ from: { opacity: 0, y: 24 }, to: { opacity: 1, y: 0 }, delay: 400, config: MOTION_TOKENS.entrance });
  const subtextAnim = useReactSpring({ from: { y: 22, opacity: 0 }, to: { y: 0, opacity: 1 }, delay: 560, config: MOTION_TOKENS.settle });
  const claimAnim = useReactSpring({ from: { y: 28, opacity: 0, scale: 0.985 }, to: { y: 0, opacity: 1, scale: 1 }, delay: 690, config: MOTION_TOKENS.settle });

  const [featuresRef, featuresInView] = useInView({ rootMargin: '-10% 0px', once: true });
  const feature1Anim = useReactSpring({ y: featuresInView ? 0 : 48, opacity: featuresInView ? 1 : 0, delay: 0, config: MOTION_TOKENS.settle });
  const feature2Anim = useReactSpring({ y: featuresInView ? 0 : 48, opacity: featuresInView ? 1 : 0, delay: 110, config: MOTION_TOKENS.settle });
  const feature3Anim = useReactSpring({ y: featuresInView ? 0 : 48, opacity: featuresInView ? 1 : 0, delay: 220, config: MOTION_TOKENS.settle });

  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.2], [0, 150]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);
  const [ctaRef, ctaInView] = useInView({ rootMargin: '-20% 0px', once: true });
  const ctaAnim = useReactSpring({ scale: ctaInView ? 1 : 0.97, opacity: ctaInView ? 1 : 0, y: ctaInView ? 0 : 28, config: MOTION_TOKENS.settle });

  const [supportRef, supportInView] = useInView({ rootMargin: '-10% 0px', once: true });
  const supportAnim = useReactSpring({ y: supportInView ? 0 : 42, opacity: supportInView ? 1 : 0, config: MOTION_TOKENS.settle });

  const [sovereigntyRef, sovereigntyInView] = useInView({ rootMargin: '-14% 0px', once: true });
  const [workflowRef, workflowInView] = useInView({ rootMargin: '-10% 0px', once: true });
  const [shareRef, shareInView] = useInView({ rootMargin: '-10% 0px', once: true });
  const [commerceRef, commerceInView] = useInView({ rootMargin: '-10% 0px', once: true });
  const [faqRef, faqInView] = useInView({ rootMargin: '-10% 0px', once: true });
  const [roadmapRef, roadmapInView] = useInView({ rootMargin: '-10% 0px', once: true });
  const workflowItems = [
    { step: "01", title: "Bring", eyebrow: "Collect", desc: "Paste your own links or upload a local CSV or JSON file for review without third party scraping", icon: <Sparkles size={26} /> },
    { step: "02", title: "Build", eyebrow: "Compose", desc: "Choose your handle then add products tips subscribers and gated sections", icon: <Layout size={26} /> },
    { step: "03", title: "Refine", eyebrow: "Improve", desc: "Share your page watch the clicks and move important links higher", icon: <BarChart2 size={26} /> }
  ];
  const ctaCapabilities = [
    { title: 'Design around your work', short: 'Design', label: 'Arrange and style your page', desc: 'Set the visual direction, choose what appears first, and give every destination a deliberate place.', icon: <Layout size={22} />, tone: 'design' },
    { title: 'Offer direct ways to support you', short: 'Sell', label: 'Products, tips, and access', desc: 'Present products, tips, and gated content without making visitors leave your page first.', icon: <ShoppingBag size={22} />, tone: 'commerce' },
    { title: 'Understand what earns attention', short: 'Learn', label: 'Views, clicks, and signals', desc: 'Use clear activity signals to understand what people notice and improve what they encounter first.', icon: <BarChart2 size={22} />, tone: 'insights' },
    { title: 'Keep clear public and private boundaries', short: 'Protect', label: 'Public and private layers', desc: 'Separate supported private content and protected messages from everything visitors can see publicly.', icon: <Lock size={22} />, tone: 'private' },
  ];
  const vaultCapabilities = [
    { icon: <Lock />, title: "Local encryption", eyebrow: "Private layer", value: "Encrypt supported vault entries before storage", desc: "Sensitive vault content is prepared in the browser before it is stored.", color: "bg-edge-coral" },
    { icon: <Mail />, title: "Protected messages", eyebrow: "Message route", value: "Protect supported sensitive contact flows", desc: "Sensitive payloads can use a protected route while ordinary messages continue normally.", color: "bg-edge-pink" },
    { icon: <ShieldCheck />, title: "Clear boundaries", eyebrow: "Public layer", value: "Keep public tools useful and private data separate", desc: "Your profile, shop, subscribers, and analytics remain usable without exposing private vault content.", color: "bg-edge-umber" },
    { icon: <Smartphone />, title: "Device unlock", eyebrow: "Device access", value: "Use a supported device credential to unlock", desc: "Supported devices can protect vault access with Windows Hello, Touch ID, or Face ID.", color: "bg-edge-pink" }
  ];
  const commerceModes = [
    { title: "Shop studio", type: "Digital product", name: "Creator launch kit", detail: "18 files · Instant access", action: "Buy now", price: "$18", desc: "Add product cards with images, pricing, and direct purchase links." },
    { title: "Tip jar", type: "Direct support", name: "Support the next release", detail: "Choose an amount · Add a note", action: "Leave a tip", price: "$12", desc: "Accept support through the payment options you connect." },
    { title: "Gated content", type: "Subscriber access", name: "Private studio notes", detail: "Email gate · Immediate unlock", action: "Unlock access", price: "Join", desc: "Put selected links and content behind a subscriber gate." }
  ];
  const shareRoutes = [
    { label: 'Social', detail: 'Use one profile URL across every social bio.', icon: <Share2 size={17} />, x: -118, y: -104 },
    { label: 'Email', detail: 'Place the same destination in campaigns and signatures.', icon: <Mail size={17} />, x: 116, y: -76 },
    { label: 'QR', detail: 'Carry your page from print and events into the browser.', icon: <Smartphone size={17} />, x: -112, y: 104 },
    { label: 'Web', detail: 'Connect portfolios, stores, communities, and any public URL.', icon: <Globe size={17} />, x: 112, y: 104 }
  ];
  const supportRoutes = [
    {
      title: 'Support desk',
      desc: 'Account access profile bugs billing safety reports and anything that needs details',
      cta: 'Open support',
      to: '/contact',
      icon: <MessageSquare size={22} />,
      color: 'bg-edge-pink text-white',
    },
    {
      title: 'Direct email',
      desc: 'Short questions partnership notes and manual follow ups that can start in your inbox',
      cta: 'Email Linkit',
      href: SUPPORT_COMPOSE_URL,
      icon: <Mail size={22} />,
      color: 'bg-edge-amber text-edge-umber',
    },
    {
      title: 'Security details',
      desc: 'Read the boundaries around vault data browser encryption device unlock and public data',
      cta: 'View blueprint',
      to: '/blueprint',
      icon: <ShieldCheck size={22} />,
      color: 'bg-edge-umber text-white',
    },
    {
      title: 'Start building',
      desc: 'Skip the inbox and claim a page when the next step is creating your public profile',
      cta: 'Create page',
      to: '/login?signup=true',
      icon: <ArrowUpRight size={22} />,
      color: 'bg-edge-coral text-white',
    },
  ];
  return (
    <div ref={containerRef} role="main" className="linkit-owned-page landing-page min-h-screen bg-edge-cream vibrant-grunge recycled-paper-overlay overflow-x-hidden selection:bg-edge-coral/30 relative flex flex-col">

      {/* Cursor Glow */}
      <CursorGlow />
      
      {/* Living Glass Navbar */}
      <LivingGlassNav
        scrollYProgress={scrollYProgress}
        activeSection={activeSection}
        onScrollToSection={scrollToSection}
        mobileMenuOpen={mobileMenuOpen}
        onToggleMobile={() => setMobileMenuOpen(prev => !prev)}
      />

      {/* 1. HERO SECTION */}
      <section
        id="hero"
        className="landing-hero landing-mood-hero pt-28 sm:pt-36 pb-32 sm:pb-40 md:pt-48 md:pb-64 px-4 md:px-8 flex flex-col items-center text-center relative z-30 interactive-zone overflow-hidden"
      >
        {/* Hero text & claim bar — fades on scroll */}
        <Motion.div
          style={{ y: heroY, opacity: heroOpacity }}
          className="landing-hero-core"
        >
        <animated.div style={titleAnim} className="landing-eyebrow mb-8 relative z-20">
          Built for creators who want more than a link list
        </animated.div>
        <h1 className="landing-editorial-title relative z-20 mb-8 flex max-w-full min-w-0 flex-col items-center gap-1 text-edge-umber md:mb-10 md:gap-2">
          <div className="flex justify-center w-full">
            <animated.span style={titleAnim} className="landing-title-line block max-w-full text-[2.65rem] leading-[0.88] sm:text-[5rem] sm:leading-[0.82] lg:text-[7.6rem] xl:text-[9.4rem]">Your audience deserves</animated.span>
          </div>
          <div className="flex justify-center w-full">
            <animated.div style={line2Anim} className="relative">
              <span className="landing-title-line block max-w-full text-[2.65rem] leading-[0.88] text-edge-umber sm:text-[5rem] sm:leading-[0.82] lg:text-[7.6rem] xl:text-[9.4rem]">more than links</span>
            </animated.div>
          </div>
        </h1>
        <animated.p style={subtextAnim} className="landing-hero-copy mb-10 w-full max-w-3xl px-2 text-pretty text-base leading-relaxed text-edge-umber/72 sm:text-xl md:mb-14 md:text-2xl">
          Build one premium home for your links products tips subscribers analytics and supported private content
        </animated.p>
        <div className="w-full max-w-3xl z-20 relative mt-2 md:mt-6 px-2">
          <animated.div style={claimAnim} className={cn(
            "hero-claim landing-claim glass-surface p-3 md:p-4 flex flex-col md:flex-row items-center gap-3 md:gap-4",
            claimPulse && 'landing-claim-pulse',
            availability === 'available' && 'landing-claim-available landing-claim-stamp',
            availability === 'taken' && 'landing-claim-taken landing-claim-reject',
            availability === 'verifying' && 'landing-claim-verifying'
          )}>
            <div className="flex-1 flex items-center gap-2 text-xl md:text-2xl font-black text-edge-umber/30 px-3 md:px-4 relative w-full overflow-hidden">
              <span className="shrink-0 whitespace-nowrap text-sm md:text-xl font-bold opacity-70">linkit.app/</span>
              <input value={claimName} onChange={(e) => setClaimName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))} type="text" placeholder="yourname" className={`bg-transparent text-edge-umber outline-none w-full placeholder:text-edge-umber/10 font-semibold tracking-tight transition-colors ${availability === 'taken' ? 'text-edge-poppy' : availability === 'available' ? 'landing-available-text' : 'text-edge-umber'}`} />
              <div className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
                {availability === 'verifying' && <div className="w-5 h-5 border-2 border-edge-pink border-t-transparent animate-spin rounded-full" />}
                {availability === 'available' && <div className="landing-status-pill landing-status-available bg-white border-2 border-edge-umber px-2 py-1 hidden sm:flex items-center gap-1 font-black text-[10px] uppercase tracking-tighter shadow-[2px_2px_0px_#000]"><Zap size={12} fill="currentColor" />Available</div>}
                {availability === 'taken' && <div className="landing-status-pill text-edge-poppy bg-white border-2 border-edge-umber px-2 py-1 hidden sm:flex items-center gap-1 font-black text-[10px] uppercase tracking-tighter shadow-[2px_2px_0px_#000]"><Lock size={12} />Taken</div>}
              </div>
            </div>
            <button onClick={handleClaim} className="landing-claim-button w-full md:w-auto px-8 md:px-12 py-4 md:py-6 bg-edge-amber border-2 border-edge-umber text-edge-umber font-bold text-lg md:text-xl cursor-pointer">Claim your name</button>
          </animated.div>
        </div>
        </Motion.div>

        {/* Profile preview — stays visible when scrolling */}
        <PremiumProfileReveal
          username={claimName}
          availability={availability}
          loading={previewLoading}
          profile={previewProfile}
          links={previewLinks}
        />

        {/* MARQUEE TICKER — floats at hero bottom over the blend */}
        <div className="absolute bottom-0 left-0 right-0 z-30">
          <DraggableMarqueeTicker />
        </div>
      </section>

      {/* 2. SOVEREIGNTY SECTION */}
      <section id="vault" ref={sovereigntyRef} data-auto-showcase="vault" className={cn("landing-mood-vault py-24 sm:py-32 md:py-64 relative overflow-hidden z-20 landing-reveal-section", sovereigntyInView && "landing-reveal-in")}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
          <div className="landing-vault-showcase grid grid-cols-1 lg:grid-cols-2 gap-16 md:gap-24 items-center">
            <div className="text-left max-w-xl">
              <div className="bg-edge-coral border-2 border-edge-umber px-6 py-2 text-white font-mono font-black text-xs uppercase tracking-widest inline-block mb-6 shadow-brutalist">Vault</div>
              <h2 className="text-[2.65rem] sm:text-[3.5rem] md:text-[5rem] text-edge-umber leading-none mb-6">Keep private things <span className="text-edge-coral">private</span></h2>
              <p className="text-lg md:text-xl text-edge-umber/60 mb-10 leading-relaxed max-w-lg">Keep supported private notes, vault content, and protected messages in a distinct layer beside your public page.</p>
              <Link to="/blueprint" className="inline-flex items-center gap-2 text-edge-coral font-black uppercase text-sm tracking-widest hover:gap-4 transition-all group">
                Read the Security Blueprint <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <div className="landing-fact-panel landing-vault-console" aria-live="polite">
              <div className="landing-vault-console-head">
                <span>{vaultCapabilities[activeVaultCapability].eyebrow} · 0{activeVaultCapability + 1}/04</span>
                <strong><i /> Device protected</strong>
              </div>
              <Motion.div
                key={vaultCapabilities[activeVaultCapability].title}
                className="landing-vault-focus"
                initial={reduceMotion ? false : { opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={MOTION_TOKENS.component}
              >
                <div className={cn("landing-vault-focus-icon", vaultCapabilities[activeVaultCapability].color)}>
                  {vaultCapabilities[activeVaultCapability].icon}
                </div>
                <span>{vaultCapabilities[activeVaultCapability].title}</span>
                <strong>{vaultCapabilities[activeVaultCapability].value}</strong>
                <p>{vaultCapabilities[activeVaultCapability].desc}</p>
              </Motion.div>
              <div className="landing-vault-boundary">
                <span><i /> Public page remains available</span>
                <ArrowRight size={17} />
                <span><i /> Private data remains distinct</span>
              </div>
            </div>
          </div>
            <div className="landing-vault-capabilities mt-16 sm:mt-20 md:mt-24 text-left" role="tablist" aria-label="Privacy capabilities">
              {vaultCapabilities.map((card, i) => (
                <Motion.button
                  type="button"
                  role="tab"
                  aria-selected={activeVaultCapability === i}
                  key={card.title}
                  className={cn("landing-vault-capability flex flex-col items-start gap-5", activeVaultCapability === i && "landing-vault-capability-active")}
                  onMouseEnter={() => { pauseShowcase('vault'); setActiveVaultCapability(i); }}
                  onFocus={() => { pauseShowcase('vault'); setActiveVaultCapability(i); }}
                  onClick={() => { pauseShowcase('vault'); setActiveVaultCapability(i); }}
                  initial={reduceMotion ? false : { opacity: 0, y: 24 }}
                  animate={sovereigntyInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: reduceMotion ? 0 : 0.5, delay: reduceMotion ? 0 : i * 0.08, ease: PREMIUM_EASE }}
                >
                  <Motion.div
                    className={cn("size-12 text-white rounded-xl flex items-center justify-center", card.color)}
                    initial={reduceMotion ? false : { scale: 0.82 }}
                    animate={sovereigntyInView ? { scale: 1 } : {}}
                    transition={{ duration: reduceMotion ? 0 : 0.46, delay: reduceMotion ? 0 : 0.12 + i * 0.08, ease: PREMIUM_EASE }}
                  >
                    {card.icon}
                  </Motion.div>
                  <span className="landing-vault-capability-index">0{i + 1}</span>
                  <h3 className="text-xl font-black tracking-tight">{card.title}</h3>
                  <p className="text-xs font-bold text-edge-umber/50 leading-relaxed">{card.desc}</p>
                  <span className="landing-vault-capability-action">Explore <ArrowRight size={15} /></span>
                </Motion.button>
              ))}
            </div>

        </div>
      </section>

      {/* 3. FEATURE GRID */}
      <section id="features" ref={featuresRef} data-auto-showcase="features" className={cn("landing-mood-features py-24 sm:py-32 md:py-64 px-4 md:px-8 relative z-30 interactive-zone landing-reveal-section", featuresInView && "landing-reveal-in")}>
        <div className="max-w-7xl mx-auto">
          <div className="landing-feature-heading mb-14 md:mb-24">
            <div className="landing-section-kicker">The page experience</div>
            <div className="landing-feature-heading-main">
              <h2>Design the journey,<br /><span>not just the page.</span></h2>
              <div className="landing-feature-heading-aside">
                <p>Shape what people notice, where they go next, and what you learn from every visit.</p>
                <div>
                  <span>01 / Design</span>
                  <span>02 / Direction</span>
                  <span>03 / Insight</span>
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        <animated.div
          style={feature1Anim}
          role="button"
          tabIndex={0}
          aria-label="Preview design controls"
          onMouseEnter={() => { pauseShowcase('features'); setActiveFeatureShowcase(0); }}
          onFocus={() => { pauseShowcase('features'); setActiveFeatureShowcase(0); }}
          onClick={() => { pauseShowcase('features'); setActiveFeatureShowcase(0); }}
          className={cn("landing-motion-card landing-feature-card glass-card p-5 sm:p-6", activeFeatureShowcase === 0 && "landing-feature-card-active")}
        >
          <FeatureShowcase type="design" active={featuresInView && !reduceMotion && activeFeatureShowcase === 0} />
          <div className="landing-feature-copy">
            <span className="landing-feature-index">01 / Design system</span>
            <h3 className="text-3xl sm:text-4xl mb-4">Design with control</h3>
              <p className="text-edge-umber/60 leading-relaxed">Shape color, structure, and motion while keeping the page focused.</p>
          </div>
        </animated.div>
        <animated.div
          style={feature2Anim}
          role="button"
          tabIndex={0}
          aria-label="Preview visitor direction"
          onMouseEnter={() => { pauseShowcase('features'); setActiveFeatureShowcase(1); }}
          onFocus={() => { pauseShowcase('features'); setActiveFeatureShowcase(1); }}
          onClick={() => { pauseShowcase('features'); setActiveFeatureShowcase(1); }}
          className={cn("landing-motion-card landing-feature-card landing-feature-card-dark glass-card bg-edge-pink/30 p-5 sm:p-6 text-white", activeFeatureShowcase === 1 && "landing-feature-card-active")}
        >
          <FeatureShowcase type="direction" active={featuresInView && !reduceMotion && activeFeatureShowcase === 1} />
          <div className="landing-feature-copy">
            <span className="landing-feature-index">02 / Visitor path</span>
            <h3 className="text-3xl sm:text-4xl mb-4">Direct every visit</h3>
            <p className="opacity-80 leading-relaxed">Lead people toward the product, booking, launch, or profile section that matters now.</p>
          </div>
        </animated.div>
        <animated.div
          style={feature3Anim}
          role="button"
          tabIndex={0}
          aria-label="Preview audience insights"
          onMouseEnter={() => { pauseShowcase('features'); setActiveFeatureShowcase(2); }}
          onFocus={() => { pauseShowcase('features'); setActiveFeatureShowcase(2); }}
          onClick={() => { pauseShowcase('features'); setActiveFeatureShowcase(2); }}
          className={cn("landing-motion-card landing-feature-card glass-card p-5 sm:p-6", activeFeatureShowcase === 2 && "landing-feature-card-active")}
        >
          <FeatureShowcase type="analytics" active={featuresInView && !reduceMotion && activeFeatureShowcase === 2} />
          <div className="landing-feature-copy">
            <span className="landing-feature-index">03 / Clear signals</span>
            <h3 className="text-3xl sm:text-4xl mb-4">Learn what works</h3>
            <p className="text-edge-umber/60 leading-relaxed">See views, clicks, and activity so your strongest destinations become easier to find.</p>
          </div>
        </animated.div>
          </div>
        </div>
      </section>

      {/* 4. COMMERCE SECTION */}
      <section id="commerce" ref={commerceRef} data-auto-showcase="commerce" className={cn("landing-mood-commerce px-4 md:px-8 py-28 md:py-40 relative z-40 interactive-zone landing-reveal-section", commerceInView && "landing-reveal-in")}>
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div className="relative">
            <Motion.div
              initial={reduceMotion ? false : { opacity: 0, scale: 0.96 }}
              animate={commerceInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: reduceMotion ? 0 : 0.65, ease: PREMIUM_EASE }}
              className="landing-commerce-demo relative mx-auto max-w-[620px] overflow-hidden border-4 border-edge-umber bg-edge-coral p-6 shadow-brutalist md:mx-0 md:p-8"
            >
              <span className="landing-demo-label absolute right-5 top-4">Demo</span>
              <div className="grid gap-5 sm:grid-cols-[0.78fr_1.22fr] sm:items-stretch">
                <Motion.div
                  className="landing-product-art relative flex min-h-[220px] flex-col justify-between overflow-hidden p-5 text-white"
                  initial={reduceMotion ? false : { opacity: 0, y: 20 }}
                  animate={commerceInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: reduceMotion ? 0 : 0.58, delay: reduceMotion ? 0 : 0.12, ease: PREMIUM_EASE }}
                >
                  <span className="relative z-10 w-fit rounded-full border border-white/25 bg-white/10 px-3 py-1 font-mono text-[8px] font-bold uppercase tracking-[0.18em] text-white/85">
                    {commerceModes[activeCommerceMode].type}
                  </span>
                  <div className="relative z-10 flex justify-center py-3">
                    <Coffee size={62} strokeWidth={1.8} className="text-white drop-shadow-[0_14px_24px_rgba(42,19,12,0.34)]" />
                  </div>
                  <div className="relative z-10 flex items-center justify-between gap-3 border-t border-white/20 pt-3 font-mono text-[8px] font-bold uppercase tracking-[0.13em] text-white/80">
                    {commerceModes[activeCommerceMode].detail.split(' · ').map((item) => <span key={item}>{item}</span>)}
                  </div>
                </Motion.div>
                <div className="flex flex-col justify-between rounded-[1.4rem] bg-white/90 p-6">
                  <Motion.div
                    initial={reduceMotion ? false : { opacity: 0, y: 16 }}
                    animate={commerceInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: reduceMotion ? 0 : 0.52, delay: reduceMotion ? 0 : 0.24, ease: PREMIUM_EASE }}
                  >
                    <p className="font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-edge-umber/38">{commerceModes[activeCommerceMode].type}</p>
                    <h3 className="mt-3 text-3xl leading-none text-edge-umber">{commerceModes[activeCommerceMode].name}</h3>
                    <p className="mt-4 text-sm font-semibold leading-relaxed text-edge-umber/55">{commerceModes[activeCommerceMode].desc}</p>
                  </Motion.div>
                  <Motion.div
                    className="mt-8 flex items-center justify-between gap-4 border-t border-edge-umber/10 pt-5"
                    initial={reduceMotion ? false : { opacity: 0, y: 14 }}
                    animate={commerceInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: reduceMotion ? 0 : 0.52, delay: reduceMotion ? 0 : 0.38, ease: PREMIUM_EASE }}
                  >
                    <strong className="tabular-nums text-2xl text-edge-umber">{commerceModes[activeCommerceMode].price}</strong>
                    <button
                      type="button"
                      className="landing-commerce-action rounded-full bg-edge-umber px-5 py-2.5 text-xs font-bold text-white"
                      onClick={() => {
                        pauseShowcase('commerce');
                        setCommerceSequenceStep((current) => (
                          current % 3 === 2 ? (current + 1) % 9 : current + 1
                        ));
                      }}
                    >
                      {commerceStage === 0 ? commerceModes[activeCommerceMode].action : commerceStage === 1 ? 'Confirm' : 'Delivered'}
                    </button>
                  </Motion.div>
                </div>
              </div>
              <Motion.div
                className="landing-commerce-confirmation mt-5 rounded-2xl border border-edge-umber/15 bg-white/70 px-5 py-4"
                initial={reduceMotion ? false : { opacity: 0, y: 18 }}
                animate={commerceInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: reduceMotion ? 0 : 0.52, delay: reduceMotion ? 0 : 0.5, ease: PREMIUM_EASE }}
              >
                <div className="landing-commerce-steps" aria-hidden="true">
                  {['Selected', 'Checkout', 'Delivered'].map((step, index) => (
                    <Motion.span
                      key={step}
                      className={cn("landing-commerce-step", index <= commerceStage && "landing-commerce-step-active")}
                      initial={false}
                      animate={commerceInView ? { opacity: index <= commerceStage ? 1 : 0.32 } : { opacity: 0.18 }}
                      transition={{ duration: reduceMotion ? 0 : 0.3, delay: reduceMotion ? 0 : 0.55 + index * 0.18 }}
                    >
                      <i />
                      {step}
                    </Motion.span>
                  ))}
                </div>
                <div className="mt-4 flex items-center justify-between gap-4">
                  <Motion.span
                    key={`${activeCommerceMode}-${commerceStage}`}
                    initial={reduceMotion ? false : { opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={MOTION_TOKENS.component}
                    className="text-sm font-bold text-edge-umber/65"
                  >
                    {commerceStage === 0 ? 'Ready to continue' : commerceStage === 1 ? 'Details confirmed' : 'Access delivered'}
                  </Motion.span>
                  <span className="font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-edge-umber/38">0% Linkit commission</span>
                </div>
              </Motion.div>
            </Motion.div>
          </div>
          <div className="space-y-10">
            <div className="bg-edge-umber text-white px-6 py-2 font-black text-xs uppercase tracking-[0.3em] inline-block shadow-brutalist">Creator Commerce</div>
            <h2 className="text-[2.35rem] sm:text-[2.5rem] md:text-[4rem] lg:text-[5.4rem] text-edge-umber leading-[0.94]">Turn discovery into <span className="text-edge-coral">direct support</span></h2>
            <div className="space-y-6">
              {commerceModes.map((feature, i) => (
                <Motion.button
                  type="button"
                  key={feature.title}
                  className={cn("landing-commerce-mode flex gap-6 items-start text-left", activeCommerceMode === i && "landing-commerce-mode-active")}
                  aria-pressed={activeCommerceMode === i}
                  onFocus={() => { pauseShowcase('commerce'); setCommerceSequenceStep(i * 3); }}
                  onClick={() => { pauseShowcase('commerce'); setCommerceSequenceStep(i * 3); }}
                  initial={reduceMotion ? false : { opacity: 0, x: 24 }}
                  animate={commerceInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: reduceMotion ? 0 : 0.5, delay: reduceMotion ? 0 : 0.12 + i * 0.1, ease: PREMIUM_EASE }}
                >
                  <Motion.div
                    className="size-8 bg-edge-umber rounded-full shrink-0 flex items-center justify-center text-white text-xs font-black"
                  >
                    {i + 1}
                  </Motion.div>
                  <div><h3 className="text-xl font-black tracking-tight text-edge-umber">{feature.title}</h3><p className="font-bold text-edge-umber/60 leading-tight">{feature.desc}</p></div>
                </Motion.button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 5. SOCIAL ECOSYSTEM (Share Anywhere) */}
      <section ref={shareRef} data-auto-showcase="share" className={cn("landing-mood-share social-section py-20 md:py-32 lg:py-48 relative overflow-hidden z-40 interactive-zone landing-reveal-section", shareInView && "landing-reveal-in")}>
        {/* Normal Content Layer */}
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16 flex flex-col lg:flex-row items-center gap-10 md:gap-16 lg:gap-24 relative z-20">
          <div className="flex-1 space-y-8 md:space-y-10">
            <div className="landing-section-kicker">Share anywhere</div>
            <h2 className="text-[2.65rem] sm:text-[3.5rem] md:text-[5.5rem] font-black tracking-tighter leading-[0.92] text-edge-umber">
              One page for <span className="text-edge-coral">every channel</span>
            </h2>
            <p className="text-xl md:text-2xl text-edge-umber/75 leading-relaxed max-w-lg">One page for every place your audience already finds you.</p>
            <p className="max-w-lg text-sm font-bold leading-relaxed text-edge-umber/55">Bring any public destination into a single, coherent route through your work.</p>
            <div className="flex flex-wrap gap-3" aria-label="Select a sharing route">
              {shareRoutes.map((destination, index) => (
                <button
                  type="button"
                  key={destination.label}
                  className={cn("landing-destination-chip", activeShareRoute === index && "landing-destination-chip-active")}
                  aria-pressed={activeShareRoute === index}
                  onMouseEnter={() => { pauseShowcase('share'); setActiveShareRoute(index); }}
                  onFocus={() => { pauseShowcase('share'); setActiveShareRoute(index); }}
                  onClick={() => { pauseShowcase('share'); setActiveShareRoute(index); }}
                >
                  {destination.label}
                </button>
              ))}
            </div>
            <Motion.p
              key={shareRoutes[activeShareRoute].label}
              className="landing-share-route-copy"
              initial={reduceMotion ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={MOTION_TOKENS.component}
            >
              <strong>{shareRoutes[activeShareRoute].label}</strong>
              {shareRoutes[activeShareRoute].detail}
            </Motion.p>
          </div>
          <div className="flex-shrink-0 w-full lg:w-[430px] relative">
             <div className="landing-share-demo relative aspect-square w-full overflow-hidden border-4 border-edge-umber bg-white p-8 shadow-brutalist-coral">
                <span className="landing-demo-label absolute right-5 top-4 z-20">Demo</span>
                <svg className="landing-share-routes" viewBox="0 0 430 430" aria-hidden="true">
                  <path className={activeShareRoute === 0 ? 'landing-share-route-active' : ''} d="M215 215 L97 111" />
                  <path className={activeShareRoute === 1 ? 'landing-share-route-active' : ''} d="M215 215 L331 139" />
                  <path className={activeShareRoute === 2 ? 'landing-share-route-active' : ''} d="M215 215 L103 319" />
                  <path className={activeShareRoute === 3 ? 'landing-share-route-active' : ''} d="M215 215 L327 319" />
                </svg>
                {!reduceMotion && (
                  <div className={cn("landing-share-signals", shareInView && "landing-share-signals-active")} aria-hidden="true">
                    <i key={activeShareRoute} className={cn("landing-share-signal", `landing-share-signal-${activeShareRoute + 1}`)} />
                  </div>
                )}
                {shareRoutes.map((destination, index) => (
                  <Motion.button
                    type="button"
                    key={destination.label}
                    className={cn("landing-share-destination absolute left-1/2 top-1/2 z-10 flex items-center gap-2 px-4 py-3", activeShareRoute === index && "landing-share-destination-active")}
                    aria-label={`Preview ${destination.label} sharing`}
                    aria-pressed={activeShareRoute === index}
                    onClick={() => { pauseShowcase('share'); setActiveShareRoute(index); }}
                    initial={reduceMotion ? false : { opacity: 0, x: -50, y: -22, scale: 0.88 }}
                    animate={shareInView ? { opacity: 1, x: destination.x - 50, y: destination.y - 22, scale: activeShareRoute === index ? 1.06 : 1 } : {}}
                    transition={{ duration: reduceMotion ? 0 : 0.52, delay: reduceMotion ? 0 : index * 0.08, ease: PREMIUM_EASE }}
                  >
                    {destination.icon}
                    <span className="text-[10px] font-bold uppercase tracking-[0.12em]">{destination.label}</span>
                  </Motion.button>
                ))}
                <Motion.div
                  className="landing-share-origin absolute left-1/2 top-1/2 z-20 flex flex-col items-center justify-center text-center"
                  initial={reduceMotion ? false : { opacity: 0, scale: 0.86 }}
                  animate={shareInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: reduceMotion ? 0 : 0.6, delay: reduceMotion ? 0 : 0.08, ease: PREMIUM_EASE }}
                >
                  <div className="mb-3 flex size-12 items-center justify-center rounded-full bg-edge-coral text-white"><Share2 size={22} /></div>
                  <strong className="text-lg text-edge-umber">{shareRoutes[activeShareRoute].label} ready</strong>
                  <span className="mt-1 text-[10px] font-bold uppercase tracking-[0.14em] text-edge-umber/40">One Linkit page</span>
                </Motion.div>
             </div>
          </div>
        </div>
      </section>

      {/* 6. WORKFLOW SECTION */}
      <section id="workflow" ref={workflowRef} data-auto-showcase="workflow" className={cn("landing-mood-workflow px-4 md:px-8 pt-24 pb-28 md:pt-32 md:pb-40 relative z-20 landing-reveal-section", workflowInView && "landing-reveal-in")}>
        <div className="max-w-7xl mx-auto">
        <div className="flex flex-col items-center text-center mb-16 md:mb-20">
          <div className="bg-edge-umber text-white px-6 py-2 font-black text-xs uppercase tracking-[0.3em] mb-6 shadow-brutalist">The Workflow</div>
          <h2 className="text-[2.45rem] sm:text-[2.7rem] md:text-[5rem] lg:text-[5.8rem] text-edge-umber leading-[0.96]">From scattered links to <span className="text-edge-coral">one considered page</span></h2>
        </div>
        <div className="landing-workflow-showcase">
          <div className="landing-workflow-stage" aria-live="polite">
            <div className="landing-workflow-stage-top">
              <span>Live page evolution</span>
              <strong>Step {workflowItems[activeWorkflowStep].step}</strong>
            </div>
            <div className="landing-workflow-device">
              <div className="landing-workflow-device-head">
                <Motion.div
                  className="landing-workflow-avatar"
                  animate={{ scale: activeWorkflowStep === 0 ? 0.88 : 1 }}
                  transition={MOTION_TOKENS.component}
                />
                <div><span /><span /></div>
              </div>
              <div className="landing-workflow-links">
                {[0, 1, 2].map((row) => (
                  <Motion.div
                    key={row}
                    className={cn('landing-workflow-link', row === 0 && activeWorkflowStep > 0 && 'landing-workflow-link-featured')}
                    animate={{
                      opacity: row <= activeWorkflowStep ? 1 : 0.28,
                      y: row <= activeWorkflowStep ? 0 : 8,
                      scale: row === 0 && activeWorkflowStep === 2 ? 1.025 : 1,
                    }}
                    transition={{ ...MOTION_TOKENS.component, delay: row * 0.04 }}
                  >
                    <span />
                    <i />
                  </Motion.div>
                ))}
              </div>
              <Motion.div
                className="landing-workflow-insight"
                animate={{
                  opacity: activeWorkflowStep === 2 ? 1 : 0,
                  y: activeWorkflowStep === 2 ? 0 : 12,
                }}
                transition={MOTION_TOKENS.component}
              >
                <BarChart2 size={15} />
                Top destination moved up
              </Motion.div>
            </div>
            <div className="landing-workflow-stage-caption">
              <span>{workflowItems[activeWorkflowStep].eyebrow}</span>
              <strong>{workflowItems[activeWorkflowStep].title} the page</strong>
            </div>
          </div>

          <div className="landing-workflow-steps" role="tablist" aria-label="How Linkit works">
            {workflowItems.map((item, idx) => (
              <Motion.button
                type="button"
                role="tab"
                aria-selected={activeWorkflowStep === idx}
                key={item.step}
                className={cn("landing-workflow-step", activeWorkflowStep === idx && "landing-workflow-step-active")}
                onMouseEnter={() => { pauseShowcase('workflow'); setActiveWorkflowStep(idx); }}
                onFocus={() => { pauseShowcase('workflow'); setActiveWorkflowStep(idx); }}
                onClick={() => { pauseShowcase('workflow'); setActiveWorkflowStep(idx); }}
                initial={reduceMotion ? false : { opacity: 0, y: 22 }}
                animate={workflowInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: reduceMotion ? 0 : 0.56, delay: reduceMotion ? 0 : idx * 0.1, ease: PREMIUM_EASE }}
              >
                <span className="landing-workflow-step-number">{item.step}</span>
                <span className="landing-workflow-step-icon">{item.icon}</span>
                <span className="landing-workflow-step-copy">
                  <strong>{item.title}</strong>
                  <span>{item.desc}</span>
                </span>
                <ArrowRight size={20} className="landing-workflow-step-arrow" />
              </Motion.button>
            ))}
          </div>
        </div>
        </div>
      </section>

      {/* 7. FAQ SECTION (Strategic Objection Handling) */}
      <section ref={faqRef} className="landing-mood-faq py-28 sm:py-40 md:py-64 px-4 sm:px-8 relative z-20">
        <div className="landing-faq-layout max-w-7xl mx-auto">
          <div className="landing-faq-intro">
            <div className="landing-section-kicker">Product clarity</div>
            <h2 className="text-[2.7rem] sm:text-[3.5rem] md:text-[5.5rem] text-edge-umber leading-[0.92] mb-6">Questions before you build</h2>
            <p className="text-edge-umber/58 text-lg md:text-xl">Direct answers about the product, its boundaries, and how it works today.</p>
            <div className="landing-faq-facts">
              <div><span>Profiles</span><strong>Multiple per account</strong></div>
              <div><span>Commerce</span><strong>0% Linkit commission</strong></div>
              <div><span>Private data</span><strong>Encrypted where supported</strong></div>
            </div>
          </div>

          <div className="landing-faq-list">
            {[
              { 
                q: "What is Linkit?",
                a: "Linkit is an exclusive, invite-only platform for creators to build highly-designed public profiles, share secure links, collect subscribers, and sell products."
              },
              {
                q: "How do I get an invite code?",
                a: "Access to Linkit is currently strictly invite-only. You must receive a unique INIT-XXXXXX code directly from the team or an existing Linkit creator who has been granted invite privileges."
              },
              {
                q: "I have an invite code. Now what?",
                a: "Click 'Join Linkit' or 'Login' at the top of the page. You will be prompted to enter your unique invite code along with your email to claim your profile and access your Studio."
              },
              {
                q: "What data is private?",
                a: "Encrypted vault entries and supported blind message payloads are encrypted in your browser before storage or relay. Public profile content, analytics, shop data, subscribers, account metadata, and fallback email flows may be processed normally to operate the service."
              },
              {
                q: "What happens if someone abuses the platform?",
                a: "Confirmed violations can result in immediate profile or account termination, including related profiles when our systems identify connected abuse."
              },
              { 
                q: "What happens if I lose my phone, sell it, or turn off my screen lock?", 
                a: (
                  <>
                    For encrypted vault entries Linkit stores ciphertext and does not keep plaintext vault keys.
                    On supported devices WebAuthn can protect local unlock and recovery material or device pairing may be needed if access is lost.
                    <br /><br />
                    If you factory reset your device lose it or disable your screen lock local vault access may be lost.
                    Keep at least one recovery path.
                    <br /><br />
                    Pair a secondary device such as your laptop.<br />
                    Print and store your 9 Code Recovery Kit in a safe place.
                  </>
                )
              },
              { 
                q: "Are there really zero commissions?", 
                a: "Linkit does not add a platform commission on tip jars or shop sales while provider fees may still apply."
              }
            ].map((faq, i) => (
              <Motion.div
                key={i} 
                className={cn(
                  "landing-faq-item transition-all cursor-pointer group",
                  openFaq === i && 'landing-faq-item-open'
                )}
                initial={reduceMotion ? false : { opacity: 0, y: 18 }}
                animate={faqInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: reduceMotion ? 0 : 0.48, delay: reduceMotion ? 0 : Math.min(i * 0.06, 0.3), ease: PREMIUM_EASE }}
                whileHover={reduceMotion ? undefined : { y: -2 }}
              >
                <button
                  type="button"
                  aria-expanded={openFaq === i}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full p-6 md:p-8 grid grid-cols-[auto_1fr_auto] items-center gap-4 text-left"
                >
                  <span className="landing-faq-index">{String(i + 1).padStart(2, '0')}</span>
                  <h3 className="text-base md:text-xl font-black uppercase tracking-tight text-edge-umber flex-1">
                    {faq.q}
                  </h3>
                  <ChevronDown 
                    size={20} 
                    className={cn(
                      "text-edge-coral shrink-0 transition-transform duration-300",
                      openFaq === i ? 'rotate-180' : 'group-hover:translate-y-0.5'
                    )} 
                  />
                </button>
                <div className={cn('faq-answer', openFaq === i && 'faq-open')}>
                  <div>
                    <div className="pl-[4.4rem] pr-6 md:pl-[5.3rem] md:pr-8 pb-6 md:pb-8 pt-0">
                      <div className="h-px bg-edge-umber/10 mb-5" />
                      <p className="text-sm font-bold text-edge-umber/60 leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                </div>
              </Motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. PRODUCT STATUS */}
      <section ref={roadmapRef} className="landing-mood-roadmap pt-32 pb-40 md:pt-40 md:pb-56 px-4 md:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-slate-200/50 [mask-image:radial-gradient(ellipse_at_center,white,transparent)] pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10 text-center flex flex-col items-center">
          <div className="inline-block px-10 py-4 bg-indigo-600 text-white font-black text-[10px] uppercase tracking-[0.5em] mb-12 md:mb-14 shadow-brutalist landing-roadmap-beacon">
            Product status
          </div>
          <h2 className="text-[2.85rem] sm:text-[3.5rem] md:text-[7.2rem] font-black uppercase tracking-tighter leading-[0.95] text-edge-umber mb-8">
            Working now <br /> <span className="text-edge-pink">building next</span>
          </h2>
          <p className="max-w-2xl text-lg md:text-xl font-bold leading-relaxed text-edge-umber/60 mb-16 md:mb-20">
            A clear view of what is available today, what is rolling out carefully, and what remains under exploration.
          </p>

          <div className="landing-roadmap-rail" aria-hidden="true">
            <div className="landing-roadmap-rail-track">
              <Motion.div
                className="landing-roadmap-rail-fill"
                initial={reduceMotion ? false : { scaleX: 0 }}
                animate={roadmapInView ? { scaleX: 1 } : {}}
                transition={{ duration: reduceMotion ? 0 : 1.05, ease: PREMIUM_EASE }}
              />
            </div>
            {['Available', 'Rolling out', 'Exploring'].map((label, index) => (
              <Motion.div
                key={label}
                className="landing-roadmap-rail-node"
                initial={reduceMotion ? false : { opacity: 0, scale: 0.8 }}
                animate={roadmapInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: reduceMotion ? 0 : 0.34, delay: reduceMotion ? 0 : 0.28 + index * 0.26 }}
              >
                <i />
                <span>{label}</span>
              </Motion.div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 sm:gap-10 mb-24 sm:mb-32 md:mb-40 w-full">
            {[
              {
                title: "Available now",
                tone: "bg-emerald-700",
                surface: "bg-emerald-50",
                icon: <Zap size={28} />,
                intro: "The working foundation creators can use today",
                items: [
                  "Public creator profiles",
                  "Links shop tips and subscribers",
                  "Analytics QR tools and multiple profiles",
                  "Design controls and supported private vault workflows"
                ]
              },
              {
                title: "Rolling out",
                tone: "bg-edge-pink",
                surface: "bg-rose-50",
                icon: <ArrowUpRight size={28} />,
                intro: "Features being released or tightened before wider access",
                items: [
                  "Custom domains",
                  "Scheduled and expiring links",
                  "Clearer vault recovery and device pairing",
                  "Expanded private messaging workflows"
                ]
              },
              {
                title: "Exploring",
                tone: "bg-amber-600",
                surface: "bg-amber-100",
                icon: <Sparkles size={28} />,
                intro: "Ideas under evaluation rather than promised features",
                items: [
                  "Campaign experiments and page variants",
                  "Automatic link recommendations",
                  "Deeper creator tool integrations",
                  "Team review and publishing workflows"
                ]
              }
            ].map((column, index) => (
              <Motion.div
                key={column.title}
                className={cn("landing-status-column relative border-4 border-edge-umber p-8 md:p-10 shadow-brutalist text-left flex flex-col items-start", column.surface)}
                initial={reduceMotion ? false : { opacity: 0, y: 28 }}
                animate={roadmapInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: reduceMotion ? 0 : 0.56, delay: reduceMotion ? 0 : index * 0.1, ease: PREMIUM_EASE }}
                whileHover={reduceMotion ? undefined : { y: -4 }}
              >
                <Motion.div
                  className={cn("size-16 text-white flex items-center justify-center mb-8", column.tone)}
                  initial={reduceMotion ? false : { scale: 0.82 }}
                  animate={roadmapInView ? { scale: 1 } : {}}
                  transition={{ duration: reduceMotion ? 0 : 0.48, delay: reduceMotion ? 0 : 0.12 + index * 0.1, ease: PREMIUM_EASE }}
                >
                  {column.icon}
                </Motion.div>
                <h3 className="text-3xl font-black uppercase tracking-tight mb-4">{column.title}</h3>
                <p className="text-sm font-bold text-edge-umber/55 leading-relaxed mb-8">{column.intro}</p>
                <div className="w-full border-t border-edge-umber/10">
                  {column.items.map((item) => (
                    <div key={item} className="flex gap-3 border-b border-edge-umber/10 py-4 last:border-b-0">
                      <span className={cn("mt-2 size-2 shrink-0 rounded-full", column.tone)} aria-hidden="true" />
                      <span className="font-bold leading-relaxed text-edge-umber/75">{item}</span>
                    </div>
                  ))}
                </div>
              </Motion.div>
            ))}
          </div>

          <div className="max-w-3xl mx-auto bg-edge-umber p-8 md:p-16 shadow-brutalist-coral">
            <h3 className="text-3xl font-black text-white uppercase tracking-tight mb-6">Join the roadmap list</h3>
            <p className="text-white/65 text-sm mb-10">Get notified when new Linkit features roll out</p>
            
            {waitlistSent ? (
              <div className="py-4 text-edge-amber font-black uppercase tracking-widest text-xl">
                {isAlreadyJoined ? 'You are already on the list' : 'You are on the list  Check your email'}
              </div>
            ) : (
              <form onSubmit={handleWaitlistSubmit} className="flex flex-col md:flex-row gap-4 mb-6">
                <input 
                  type="email" 
                  required 
                  value={waitlistEmail}
                  onChange={(e) => setWaitlistEmail(e.target.value)}
                  placeholder="your@email.com" 
                  className="flex-1 px-6 py-4 bg-white/10 border-2 border-white/20 text-white font-bold outline-none focus:border-edge-coral transition-all"
                />
                <button type="submit" disabled={waitlistSubmitting} className="px-10 py-4 bg-edge-coral text-white font-black uppercase text-sm shadow-brutalist hover:bg-white hover:text-edge-umber transition-all disabled:opacity-50">
                  {waitlistSubmitting ? 'Joining' : 'Get roadmap updates'}
                </button>
              </form>
            )}
            
            <p className="text-[10px] text-white/30 font-black uppercase tracking-widest leading-relaxed">
              Privacy pledge  Linkit does not add platform ad trackers by default  Creators may connect their own pixels while essential analytics security hosting database and email providers help run the service
            </p>
          </div>
        </div>
      </section>

      {/* 9. CALL TO ACTION */}
      <animated.section
        ref={ctaRef}
        style={ctaAnim}
        data-auto-showcase="cta"
        className="landing-mood-cta landing-final-stage py-24 md:py-40 px-4 md:px-8 relative overflow-hidden"
      >
        <div className="landing-cta-showcase max-w-7xl mx-auto">
          <div className="landing-cta-copy">
            <div className="landing-section-kicker">Shape your page</div>
            <h2 className="text-[2.8rem] sm:text-[4.8rem] md:text-[6.3rem] text-edge-umber">Make every visit lead somewhere.</h2>
            <p>Choose what visitors notice first, what they can support, what you learn, and what stays private.</p>
            <Link to="/login?signup=true" className="landing-final-cta inline-flex items-center gap-3 px-9 py-5 md:px-12 md:py-6 bg-edge-amber border-2 border-edge-umber text-edge-umber font-bold text-xl md:text-2xl">
              Start building <ArrowUpRight size={22} />
            </Link>
          </div>

          <div className="landing-cta-product">
            <div className="landing-cta-product-head">
              <span>Your page takes shape</span>
              <strong>{ctaCapabilities[activeCtaCapability].title}</strong>
            </div>
            <Motion.p
              key={`cta-copy-${activeCtaCapability}`}
              className="landing-cta-product-copy"
              initial={reduceMotion ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={MOTION_TOKENS.component}
            >
              {ctaCapabilities[activeCtaCapability].desc}
            </Motion.p>
            <div className={cn("landing-cta-preview", `landing-cta-preview-${ctaCapabilities[activeCtaCapability].tone}`)} aria-live="polite">
              <Motion.div
                key={`cta-preview-${activeCtaCapability}`}
                className="landing-cta-preview-scene"
                initial={reduceMotion ? false : { opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={MOTION_TOKENS.component}
              >
                {activeCtaCapability === 0 && (
                  <>
                    <div className="landing-cta-scene-head"><span>Page composition</span><strong>Warm editorial</strong></div>
                    <div className="landing-cta-design-layout">
                      <div className="landing-cta-design-profile"><i /><span>Studio North</span><small>Independent creative practice</small></div>
                      <div className="landing-cta-design-link landing-cta-design-link-featured">Featured launch <ArrowUpRight size={15} /></div>
                      <div className="landing-cta-design-link">Book a session <ArrowUpRight size={15} /></div>
                    </div>
                    <div className="landing-cta-palette"><i /><i /><i /><span>Layout · Type · Color</span></div>
                  </>
                )}
                {activeCtaCapability === 1 && (
                  <>
                    <div className="landing-cta-scene-head"><span>Creator commerce</span><strong>Ready to purchase</strong></div>
                    <div className="landing-cta-product-card">
                      <div><small>Digital product</small><strong>Creator launch kit</strong><span>Templates, notes, and assets</span></div>
                      <div><strong>$18</strong><span>Buy now</span></div>
                    </div>
                    <div className="landing-cta-commerce-options"><span>Tips enabled</span><span>Gated access</span><span>0% platform commission</span></div>
                  </>
                )}
                {activeCtaCapability === 2 && (
                  <>
                    <div className="landing-cta-scene-head"><span>Page activity</span><strong>Last 30 days</strong></div>
                    <div className="landing-cta-metrics"><div><span>Views</span><strong>2,418</strong></div><div><span>Clicks</span><strong>684</strong></div><div><span>Click rate</span><strong>28.3%</strong></div></div>
                    <div className="landing-cta-chart">
                      {[42, 58, 48, 76, 66, 92].map((height, index) => (
                        <Motion.i key={index} initial={reduceMotion ? false : { height: '8%' }} animate={{ height: `${height}%` }} transition={{ ...MOTION_TOKENS.component, delay: index * 0.04 }} />
                      ))}
                    </div>
                  </>
                )}
                {activeCtaCapability === 3 && (
                  <>
                    <div className="landing-cta-scene-head"><span>Privacy boundary</span><strong>Device protected</strong></div>
                    <div className="landing-cta-layer landing-cta-layer-public"><span>Public page</span><strong>Links · Shop · Analytics</strong><i>Visible</i></div>
                    <div className="landing-cta-layer landing-cta-layer-private"><span>Private layer</span><strong>Vault · Protected messages</strong><i><Lock size={14} /> Locked</i></div>
                    <div className="landing-cta-protect-note"><ShieldCheck size={18} /> Private content remains distinct from your public profile.</div>
                  </>
                )}
              </Motion.div>
            </div>
            <div className="landing-cta-capabilities" role="tablist" aria-label="Preview Linkit capabilities">
              {ctaCapabilities.map((capability, index) => (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeCtaCapability === index}
                  key={capability.title}
                  className={cn("landing-cta-capability", activeCtaCapability === index && "landing-cta-capability-active")}
                  onMouseEnter={() => { pauseShowcase('cta'); setActiveCtaCapability(index); }}
                  onFocus={() => { pauseShowcase('cta'); setActiveCtaCapability(index); }}
                  onClick={() => { pauseShowcase('cta'); setActiveCtaCapability(index); }}
                >
                  <span>{capability.icon}</span>
                  <div>
                    <strong>{capability.short}</strong>
                    <small>{capability.label}</small>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </animated.section>

      {/* 10. CONTACT HUB */}
      <animated.section id="support" ref={supportRef} style={supportAnim} data-auto-showcase="support" className="landing-mood-support py-24 sm:py-32 px-4 sm:px-8 relative z-20">
        <div className="max-w-7xl mx-auto">
          <div className="landing-support-heading">
            <div className="landing-section-kicker">Talk to Linkit</div>
            <h2>Start in the right place</h2>
            <p>Choose the route that best fits your question, security concern, or next step.</p>
          </div>
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-stretch">
            <div className="landing-contact-panel relative overflow-hidden glass-surface-strong p-8 sm:p-10 md:p-14 text-edge-umber">
              <div className="relative z-10 flex min-h-[440px] flex-col justify-between gap-12">
                <div>
                  <span className="landing-contact-panel-label">Your selected route</span>
                  <Motion.div
                    key={supportRoutes[activeSupportRoute].title}
                    className="landing-contact-selected-icon"
                    initial={reduceMotion ? false : { opacity: 0, scale: 0.88 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={MOTION_TOKENS.component}
                  >
                    {supportRoutes[activeSupportRoute].icon}
                  </Motion.div>
                  <Motion.h3
                    key={`title-${supportRoutes[activeSupportRoute].title}`}
                    initial={reduceMotion ? false : { opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={MOTION_TOKENS.component}
                  >
                    {supportRoutes[activeSupportRoute].title}
                  </Motion.h3>
                </div>
                  <Motion.p
                    key={`desc-${supportRoutes[activeSupportRoute].title}`}
                    className="max-w-lg text-base sm:text-xl font-bold leading-relaxed text-edge-umber/70"
                  initial={reduceMotion ? false : { opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={MOTION_TOKENS.component}
                >
                  {supportRoutes[activeSupportRoute].desc}
                </Motion.p>
                <div className="landing-contact-routing" aria-hidden="true">
                  {supportRoutes.map((route, index) => (
                    <span key={route.title} className={activeSupportRoute === index ? 'landing-contact-route-active' : ''}>
                      <i /> {route.title}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              {supportRoutes.map((route, index) => {
                const card = (
                  <Motion.div
                    className={cn("landing-support-route group flex h-full min-h-[220px] flex-col justify-between glass-surface p-6 sm:p-7", activeSupportRoute === index && "landing-support-route-active")}
                    initial={reduceMotion ? false : { opacity: 0, y: 22 }}
                    animate={supportInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: reduceMotion ? 0 : 0.52, delay: reduceMotion ? 0 : index * 0.08, ease: PREMIUM_EASE }}
                    whileHover={reduceMotion ? undefined : { y: -4 }}
                  >
                    <div>
                      <Motion.div
                        className={cn('mb-8 flex size-14 items-center justify-center rounded-full', route.color)}
                      >
                        {route.icon}
                      </Motion.div>
                      <h3 className="text-2xl font-black uppercase leading-none tracking-tight text-edge-umber">{route.title}</h3>
                      <p className="mt-4 text-sm font-bold leading-relaxed text-edge-umber/62">{route.desc}</p>
                    </div>
                    <div className="mt-8 inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.24em] text-edge-umber/48 transition-colors group-hover:text-edge-pink">
                      {route.cta}
                      <ArrowRight size={14} />
                    </div>
                  </Motion.div>
                );

                return route.href ? (
                  <a key={route.title} href={route.href} target="_blank" rel="noopener noreferrer" className="block h-full" onMouseEnter={() => { pauseShowcase('support'); setActiveSupportRoute(index); }} onFocus={() => { pauseShowcase('support'); setActiveSupportRoute(index); }}>
                    {card}
                  </a>
                ) : (
                  <Link key={route.title} to={route.to} className="block h-full" onMouseEnter={() => { pauseShowcase('support'); setActiveSupportRoute(index); }} onFocus={() => { pauseShowcase('support'); setActiveSupportRoute(index); }}>
                    {card}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </animated.section>

      <footer className="landing-mood-footer px-6 py-16 md:px-10 md:py-24">
        <div className="mx-auto max-w-7xl flex flex-col gap-14">
          <div className="landing-footer-stage">
            <div>
              <span className="landing-footer-eyebrow">One page, clearly yours</span>
              <h2>Bring the work together.</h2>
              <p>Give every link, product, and audience touchpoint a considered place to live.</p>
            </div>
            <Link to="/login?signup=true" className="landing-footer-claim">
              <span>linkit.app/</span>
              <strong>yourname</strong>
              <ArrowUpRight size={22} />
            </Link>
          </div>
          <div className="landing-footer-directory">
            <div className="space-y-6">
              <Link to="/" className="group inline-flex items-center gap-3 transition-transform hover:-translate-y-0.5">
                <div className="flex h-10 w-10 items-center justify-center border-2 border-edge-umber bg-edge-coral text-xl font-black text-edge-umber transition-transform">
                  L
                </div>
                <span className="landing-wordmark text-2xl text-edge-umber">linkit</span>
              </Link>
              <p className="max-w-xs text-sm font-bold leading-relaxed text-edge-umber/55">
                A considered home for your public profile, products, audience, and supported private content.
              </p>
              <a href={SUPPORT_COMPOSE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2.5 text-[10px] font-black uppercase tracking-widest text-edge-umber/45 hover:text-edge-pink transition-colors">
                <Mail size={14} />
                {SUPPORT_EMAIL}
              </a>
            </div>

            <nav className="grid gap-8 sm:grid-cols-3" aria-label="Footer navigation">
              <div className="space-y-4">
                <h3 className="text-[10px] font-black uppercase tracking-[0.24em] text-edge-umber/35">Explore</h3>
                <div className="grid gap-3 text-[10px] font-mono font-black uppercase tracking-widest text-edge-umber/50">
                  {FOOTER_GROWTH_LINKS.map((page) => (
                      <Link key={page.slug} to={`/${page.slug}`} className="landing-footer-link">
                      {withoutFullStops(page.eyebrow)}
                    </Link>
                  ))}
                  <Link to="/blueprint" className="landing-footer-link">Blueprint</Link>
                  <Link to="/discover" className="landing-footer-link">Discover</Link>
                  <Link to="/developer-program" className="landing-footer-link">API Portal</Link>
                  <Link to="/portal" className="landing-footer-link">Buyer Portal</Link>
                </div>
              </div>
              {/* <div className="space-y-4">
                <h3 className="text-[10px] font-black uppercase tracking-[0.24em] text-edge-umber/35">Templates</h3>
                <div className="grid gap-3 text-[10px] font-mono font-black uppercase tracking-widest text-edge-umber/50">
                  {FOOTER_TEMPLATE_LINKS.map((page) => (
                    <Link key={page.slug} to={`/${page.slug}`} className="landing-footer-link">
                      {withoutFullStops(page.eyebrow)}
                    </Link>
                  ))}
                </div>
              </div> */}
              <div className="space-y-4">
                <h3 className="text-[10px] font-black uppercase tracking-[0.24em] text-edge-umber/35">Legal</h3>
                <div className="grid gap-3 text-[10px] font-mono font-black uppercase tracking-widest text-edge-umber/50">
                  {FOOTER_LEGAL_LINKS.map((link) => (
                    <Link key={link.to} to={link.to} className="landing-footer-link">
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            </nav>
          </div>

          <div className="flex flex-col gap-5 border-t-2 border-edge-umber/10 pt-8 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-6 w-6 items-center justify-center bg-edge-umber text-xs font-black text-white">L</div>
              <p className="text-[10px] font-mono font-black uppercase tracking-widest text-edge-umber/35">
                Linkit 2026  All rights reserved
              </p>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default Landing;
